import React from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { motion } from 'framer-motion';
import { Phone, DollarSign, Truck, Clock, Check } from 'lucide-react';
import { scrollToCTAEvent } from './CarForm';


export const BrandsAndVehicles: React.FC = () => {
  const brandSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        }
      }
    ]
  };

  /* const vehicleSettings = {
    ...brandSettings,
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  }; */

  const services = [
    {
      icon: DollarSign,
      title: "Top Cash Offers",
      description: "Get up to $10,000 for your vehicle"
    },
    {
      icon: Truck,
      title: "Free Towing",
      description: "Free car removal anywhere in Adelaide"
    },
    {
      icon: Clock,
      title: "Same Day Service",
      description: "Quick and hassle-free process"
    },
    {
      icon: Check,
      title: "Any Condition",
      description: "We buy cars in any condition"
    }
  ];

  return (
    <>
      {/* Value Proposition Section */}
      <section className="py-16 bg-[#fdc751] text-gray-900 relative overflow-hidden">
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'url("/img/overlay2.png")',
          opacity: 0.3,
          mixBlendMode: 'exclusion',
          backgroundAttachment: 'fixed',
          zIndex: 0
        }}
      />
        <div className="container px-4 mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold mb-4">Get Top Dollar For Your Car Today</h2>
              <p className="text-xl mb-6">We buy all types of vehicles in any condition:</p>
              <ul className="grid grid-cols-2 gap-4 mb-8">
                {['Cars', 'Trucks', 'Vans', 'SUVs', '4x4s', 'Utes', 'Damaged Cars', 'Old Cars'].map((item) => (
                  <li key={item} className="flex items-center">
                    <Check className="w-5 h-5 mr-2" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const heroSection = document.getElementById('hero-section');
                    if (heroSection) {
                      heroSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="px-8 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Get Free Quote
                </motion.button>
                <motion.a
                  href="tel:+61451326175"
                  whileHover={{ scale: 1.05 }}
                  className="px-8 py-3 bg-green-500 rounded-lg font-semibold flex items-center justify-center hover:bg-green-600 transition-colors text-white"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </motion.a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-6"
            >
              {services.map((service) => (
                <div key={service.title} className="bg-white/10 p-6 rounded-lg backdrop-blur-sm">
                  <service.icon className="w-10 h-10 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Simple Responsive Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-16 w-full max-w-[1200px] mx-auto"
          >
            <img
              src="/img/top_dollar_offer.png"
              alt="Top Cash Offer for Your Car"
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="relative py-20 bg-gray-50">
        <div className="container relative z-10 px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-lg font-semibold text-blue-600">Any Make or Model</span>
            <h2 className="mt-2 text-3xl font-bold">We Buy All Popular Brands</h2>
            <p className="mt-4 text-gray-600">Get instant cash offers for your vehicle, regardless of brand or condition</p>
          </motion.div>

          <Slider {...brandSettings} className="brand-slider">
            {[
              { img: '/brands/toyota.png', name: 'Toyota', models: 'Hilux, RAV4, Corolla' },
              { img: '/brands/ford.png', name: 'Ford', models: 'Ranger, Everest' },
              { img: '/brands/mazda.png', name: 'Mazda', models: 'CX-5, Mazda3' },
              { img: '/brands/hyundai.png', name: 'Hyundai', models: 'i30, Tucson' },
              { img: '/brands/mitsubishi.png', name: 'Mitsubishi', models: 'Outlander, Triton' },
              { img: '/brands/kia.png', name: 'Kia', models: 'Sportage, Cerato' },
              { img: '/brands/isuzu.png', name: 'Isuzu', models: 'D-Max, MU-X' },
              { img: '/brands/nissan.png', name: 'Nissan', models: 'X-Trail, Navara' },
              { img: '/brands/volkswagen.png', name: 'Volkswagen', models: 'Golf, Amarok' },
              { img: '/brands/subaru.png', name: 'Subaru', models: 'Forester, Outback' },
            ].map((brand) => (
              <motion.div
                key={brand.name}
                whileHover={{ scale: 1.05 }}
                className="px-4"
              >
                <div className="p-6 bg-white ">
                  <img 
                    src={brand.img} 
                    alt={brand.name}
                    className="object-contain w-32 h-32 mx-auto"
                  />
                  <h3 className="mt-4 text-lg font-semibold text-center">{brand.name}</h3>
                  {/* <p className="text-sm text-gray-600 text-center">{brand.models}</p> */}
                </div>
              </motion.div>
            ))}
          </Slider>
        </div>
      </section>

      {/* Recent Purchases Section */}
      {/*  <section className="relative py-20 bg-white">
        <div className="container relative z-10 px-4 mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-lg font-semibold text-blue-600">Recent Transactions</span>
            <h2 className="mt-2 text-3xl font-bold">Cars We've Recently Purchased</h2>
            <p className="mt-4 text-gray-600">
              See some of our recent purchases - we buy vehicles in any condition
            </p>
          </motion.div>

          <Slider {...vehicleSettings} className="vehicle-slider">
            {[
              {
                name: 'Toyota HiLux',
                img: '/img/vehicles/hilux.png',
                year: '2021',
                mileage: '45,000 km',
                transmission: 'Automatic',
                condition: 'Used'
              },
              {
                name: 'Mazda CX-5',
                img: '/img/vehicles/cx5.png',
                year: '2020',
                mileage: '38,000 km',
                transmission: 'Automatic',
                condition: 'Damaged'
              },
              {
                name: 'Ford Ranger',
                img: '/img/vehicles/ranger.png',
                year: '2022',
                mileage: '25,000 km',
                transmission: 'Automatic',
                condition: 'Like New'
              },
              {
                name: 'Hyundai Tucson',
                img: '/img/vehicles/tucson.png',
                year: '2021',
                mileage: '32,000 km',
                transmission: 'Automatic',
                condition: 'Used'
              }
            ].map((vehicle) => (
              <motion.div
                key={vehicle.name}
                whileHover={{ scale: 1.03 }}
                className="px-4"
              >
                <div className="overflow-hidden bg-white rounded-lg shadow-lg">
                  <div className="relative pb-[60%]">
                    <img 
                      src={vehicle.img} 
                      alt={vehicle.name}
                      className="absolute top-0 left-0 object-cover w-full h-full"
                    />
                    <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                      {vehicle.condition}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="mb-3 text-xl font-semibold">{vehicle.name}</h3>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-center">
                        <span className="w-24">Year:</span>
                        <span className="font-medium">{vehicle.year}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-24">Mileage:</span>
                        <span className="font-medium">{vehicle.mileage}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-24">Transmission:</span>
                        <span className="font-medium">{vehicle.transmission}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </Slider>
        </div>
      </section> */} 
    </>
  );
}; 